import * as Popover from "@radix-ui/react-popover";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import { DayPicker } from "react-day-picker";
import { cn } from "../lib/cn.ts";
import { FIELD_BASE } from "./input.tsx";

export interface DatePickerProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  min?: string;
  max?: string;
  "aria-label"?: string;
  /** Clases para el campo. Sirve para igualar su alto con el de al lado. */
  className?: string;
}

const iso = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parse = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return undefined;
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  return Number.isNaN(date.getTime()) ? undefined : date;
};

/**
 * Deja tecleada la fecha en `AAAA-MM-DD` poniendo los guiones sola: se escriben
 * ocho dígitos y nada más.
 *
 * `closing` es la diferencia entre escribir y borrar. Al escribir, el guión
 * aparece apenas se completa el año o el mes, que es lo que se espera al
 * teclear de corrido. Al borrar no se agrega ninguno: si se repusiera, el
 * retroceso quedaría trabado contra un guión que vuelve a aparecer y el campo
 * no se podría vaciar.
 */
const mask = (value: string, closing: boolean) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  const groups = [digits.slice(0, 4), digits.slice(4, 6), digits.slice(6, 8)];
  const typed = groups.filter((group) => group !== "").join("-");
  const complete = digits.length === 4 || digits.length === 6;
  return closing && complete ? `${typed}-` : typed;
};

/**
 * Los extremos que puede recorrer el calendario.
 *
 * Con los desplegables activados, `react-day-picker` acota por su cuenta la
 * navegación entre cien años atrás y el fin del año en curso. Ese rango sirve
 * para una fecha de nacimiento y rompe todo lo demás: la vigencia de una
 * agenda, una fecha de facturación o el próximo tratamiento viven en el futuro
 * y quedarían encerrados en diciembre de este año. Por eso los extremos se
 * fijan siempre a mano, a partir de `min` y `max` cuando el campo los declara.
 *
 * El respaldo es asimétrico a propósito: cien años hacia atrás porque es la
 * vida de un paciente, y diez hacia adelante porque ningún campo del producto
 * agenda más lejos que eso.
 */
const navigableRange = (min?: string, max?: string) => {
  const year = new Date().getFullYear();
  const at = (value: string | undefined, fallback: Date) =>
    (value ? parse(value) : undefined) ?? fallback;
  return {
    startMonth: at(min, new Date(year - 100, 0, 1)),
    endMonth: at(max, new Date(year + 10, 11, 31)),
  };
};

/** Meses en el idioma del producto, no en el de la librería. */
const MONTHS = Array.from({ length: 12 }, (_, month) =>
  new Date(2000, month, 1).toLocaleDateString("es", { month: "long" }),
);

/** Un mes como número corrido, para comparar rangos sin pelear con fechas. */
const ordinal = (date: Date) => date.getFullYear() * 12 + date.getMonth();

/** Años por página en la vista de años. Cuatro columnas por cinco filas. */
const YEARS_PER_PAGE = 20;

const CELL =
  "rounded-xl py-2 text-sm capitalize hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-30";
const CELL_SELECTED = "bg-primary text-primary-foreground hover:bg-primary";

function HeaderButton(props: {
  onClick?: () => void;
  disabled?: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      aria-label={props.label}
      className="grid h-8 w-8 place-items-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-30"
    >
      {props.children}
    </button>
  );
}

/**
 * Calendario de tres niveles: días, meses y años.
 *
 * El rótulo del encabezado no decora, navega. Desde los días abre la grilla de
 * meses; desde los meses, la de años; y elegir baja de nuevo un nivel hasta
 * volver a los días. Es el gesto que tiene cualquier calendario de sistema
 * operativo, y evita lo único que no se puede pedir en una ficha clínica:
 * llegar a 1960 a fuerza de flechas, un mes por clic.
 *
 * Se descartó el desplegable nativo que ofrece la librería. Con cien años de
 * historia clínica encima, el `select` abre una lista de ciento un elementos
 * que el navegador despliega hacia arriba y tapa el formulario entero.
 */
export function DatePicker(props: DatePickerProps) {
  const selected = parse(props.value);
  const { startMonth, endMonth } = navigableRange(props.min, props.max);
  const disabledRange = [
    ...(props.min ? [{ before: parse(props.min)! }] : []),
    ...(props.max ? [{ after: parse(props.max)! }] : []),
  ];

  const [view, setView] = useState<"days" | "months" | "years">("days");
  const [month, setMonth] = useState(() => selected ?? new Date());

  const year = month.getFullYear();
  const firstYear = startMonth.getFullYear();
  const lastYear = endMonth.getFullYear();
  /* Las páginas se alinean a bloques fijos para que la grilla no se corra de
     lugar entre una y otra. */
  const pageStart = Math.floor(year / YEARS_PER_PAGE) * YEARS_PER_PAGE;

  const go = (next: Date) => {
    const min = ordinal(startMonth);
    const max = ordinal(endMonth);
    const clamped = Math.min(Math.max(ordinal(next), min), max);
    setMonth(new Date(Math.floor(clamped / 12), clamped % 12, 1));
  };

  /* Al abrir se vuelve siempre a los días y al mes de la fecha cargada: quien
     reabre el calendario espera encontrarlo donde lo dejó el valor, no donde
     lo dejó la navegación anterior. */
  const onOpenChange = (open: boolean) => {
    if (!open) return;
    setView("days");
    setMonth(selected ?? new Date());
  };

  const header = (
    <div className="mb-2 flex items-center justify-between gap-1">
      {view === "years" ? (
        <HeaderButton
          label="Veinte años anteriores"
          disabled={pageStart - 1 < firstYear}
          onClick={() =>
            go(new Date(year - YEARS_PER_PAGE, month.getMonth(), 1))
          }
        >
          <ChevronLeft className="h-4 w-4" />
        </HeaderButton>
      ) : (
        <HeaderButton
          label={view === "days" ? "Mes anterior" : "Año anterior"}
          disabled={
            ordinal(
              view === "days"
                ? new Date(year, month.getMonth() - 1, 1)
                : new Date(year - 1, 11, 1),
            ) < ordinal(startMonth)
          }
          onClick={() =>
            go(
              view === "days"
                ? new Date(year, month.getMonth() - 1, 1)
                : new Date(year - 1, month.getMonth(), 1),
            )
          }
        >
          <ChevronLeft className="h-4 w-4" />
        </HeaderButton>
      )}

      <button
        type="button"
        onClick={() => setView(view === "days" ? "months" : "years")}
        disabled={view === "years"}
        className="flex-1 rounded-lg py-1 text-sm font-semibold capitalize hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:hover:bg-transparent"
      >
        {view === "days" && `${MONTHS[month.getMonth()]} ${year}`}
        {view === "months" && year}
        {view === "years" && `${pageStart} – ${pageStart + YEARS_PER_PAGE - 1}`}
      </button>

      {view === "years" ? (
        <HeaderButton
          label="Veinte años siguientes"
          disabled={pageStart + YEARS_PER_PAGE > lastYear}
          onClick={() =>
            go(new Date(year + YEARS_PER_PAGE, month.getMonth(), 1))
          }
        >
          <ChevronRight className="h-4 w-4" />
        </HeaderButton>
      ) : (
        <HeaderButton
          label={view === "days" ? "Mes siguiente" : "Año siguiente"}
          disabled={
            ordinal(
              view === "days"
                ? new Date(year, month.getMonth() + 1, 1)
                : new Date(year + 1, 0, 1),
            ) > ordinal(endMonth)
          }
          onClick={() =>
            go(
              view === "days"
                ? new Date(year, month.getMonth() + 1, 1)
                : new Date(year + 1, month.getMonth(), 1),
            )
          }
        >
          <ChevronRight className="h-4 w-4" />
        </HeaderButton>
      )}
    </div>
  );

  return (
    <Popover.Root onOpenChange={onOpenChange}>
      <div className="relative">
        <input
          id={props.id}
          type="text"
          inputMode="numeric"
          placeholder="AAAA-MM-DD"
          aria-label={props["aria-label"]}
          aria-invalid={props.invalid || undefined}
          disabled={props.disabled}
          value={props.value}
          className={cn(
            FIELD_BASE,
            "h-10 pr-10 tabular-nums",
            props.invalid && "border-danger",
            props.className,
          )}
          onInput={(event) => {
            const typed = (event.target as HTMLInputElement).value;
            props.onChange(mask(typed, typed.length >= props.value.length));
          }}
        />
        <Popover.Trigger asChild>
          <button
            type="button"
            disabled={props.disabled}
            aria-label="Abrir calendario"
            className="absolute inset-y-0 right-0 grid w-10 place-items-center rounded-r-xl text-muted-foreground hover:text-primary"
          >
            <CalendarDays className="h-4 w-4" />
          </button>
        </Popover.Trigger>
      </div>
      <Popover.Portal>
        <Popover.Content
          sideOffset={6}
          align="start"
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          /* Mismo motivo que en la lista de horas: dentro de un modal esto
             cuelga de otro portal y sin `pointerEvents` el calendario no
             recibe los clics. */
          style={{ pointerEvents: "auto" }}
          className="z-[220] rounded-2xl border border-border bg-card p-3 shadow-2xl"
        >
          <div className="w-[15.75rem]">
            {header}

            {view === "days" && (
              <DayPicker
                mode="single"
                selected={selected}
                month={month}
                onMonthChange={setMonth}
                disabled={disabledRange}
                startMonth={startMonth}
                endMonth={endMonth}
                /* El encabezado propio de la librería se oculta: el de arriba
                   hace lo mismo y además navega entre niveles. */
                hideNavigation
                formatters={{
                  formatWeekdayName: (day) =>
                    day.toLocaleDateString("es", { weekday: "narrow" }),
                }}
                onSelect={(date) => date && props.onChange(iso(date))}
                classNames={{
                  months: "flex",
                  month: "space-y-2",
                  month_caption: "hidden",
                  month_grid: "border-collapse",
                  weekdays: "flex",
                  weekday:
                    "w-9 text-center text-xs font-medium uppercase text-muted-foreground",
                  week: "mt-1 flex",
                  day: "h-9 w-9 text-center text-sm",
                  day_button:
                    "h-9 w-9 rounded-xl hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  selected:
                    "rounded-xl bg-primary text-primary-foreground hover:bg-primary",
                  today: "font-bold text-primary",
                  outside: "text-muted-foreground/40",
                  disabled: "pointer-events-none opacity-30",
                }}
              />
            )}

            {view === "months" && (
              <div className="grid grid-cols-3 gap-1">
                {MONTHS.map((name, index) => {
                  const value = new Date(year, index, 1);
                  return (
                    <button
                      key={name}
                      type="button"
                      disabled={
                        ordinal(value) < ordinal(startMonth) ||
                        ordinal(value) > ordinal(endMonth)
                      }
                      onClick={() => {
                        setMonth(value);
                        setView("days");
                      }}
                      className={cn(
                        CELL,
                        selected?.getFullYear() === year &&
                          selected.getMonth() === index &&
                          CELL_SELECTED,
                      )}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            )}

            {view === "years" && (
              <div className="grid grid-cols-4 gap-1">
                {Array.from(
                  { length: YEARS_PER_PAGE },
                  (_, index) => pageStart + index,
                ).map((option) => (
                  <button
                    key={option}
                    type="button"
                    disabled={option < firstYear || option > lastYear}
                    onClick={() => {
                      setMonth(new Date(option, month.getMonth(), 1));
                      setView("months");
                    }}
                    className={cn(
                      CELL,
                      "tabular-nums",
                      selected?.getFullYear() === option && CELL_SELECTED,
                    )}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
